/**
 * Token Refresh Interceptor
 * Automatically refreshes JWT access tokens when they expire
 */

import axios from 'axios';
import { store } from '../../store';
import { logout, setCredentials } from '../../Features/Authentication/authSlice';
import { getAuthBaseUrl } from '../../Features/API/api';
import { setToken, setRefreshToken } from './tokenStorage';

// Track if we're currently refreshing
let isRefreshing = false;
let refreshSubscribers = [];

// Add subscribers waiting for token refresh
function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

// Notify all subscribers when token is refreshed
function onTokenRefreshed(token) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

/**
 * Setup axios interceptors for automatic token refresh
 */
export function setupTokenRefreshInterceptor(axiosInstance) {
  // Response interceptor to handle 401 errors
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Check if error is 401 and we haven't already tried to refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        // Don't retry for login/refresh endpoints
        if (originalRequest.url?.includes('/token/') || originalRequest.url?.includes('/login/')) {
          return Promise.reject(error);
        }

        if (!isRefreshing) {
          isRefreshing = true;
          originalRequest._retry = true;

          try {
            const state = store.getState();
            const refreshToken = state.auth.refreshToken;

            if (!refreshToken) {
              throw new Error('No refresh token available');
            }

            // Call refresh endpoint
            const response = await axios.post(`${getAuthBaseUrl()}auth/refresh/`, {
              refresh: refreshToken,
            });

            const { access, refresh } = response.data;

            // Update store with new tokens
            store.dispatch(
              setCredentials({
                token: access,
                refreshToken: refresh || refreshToken,
                user: state.auth.user,
                organizations: state.auth.organizations,
                defaultOrganization: state.auth.defaultOrganization,
                permissions: state.auth.permissions,
                roles: state.auth.roles,
                maxRoleLevel: state.auth.maxRoleLevel,
                currentOrganization: state.auth.currentOrganization,
              })
            );

            // Update session storage
            setToken(access);
            if (refresh) {
              setRefreshToken(refresh);
            }

            isRefreshing = false;
            onTokenRefreshed(access);

            // Retry original request with new token
            originalRequest.headers['Authorization'] = `Bearer ${access}`;
            return axiosInstance(originalRequest);
          } catch (refreshError) {
            isRefreshing = false;

            // Refresh failed, logout user
            store.dispatch(logout());
            window.location.href = '/login';

            return Promise.reject(refreshError);
          }
        } else {
          // Token is being refreshed, queue this request
          return new Promise((resolve, reject) => {
            subscribeTokenRefresh((token) => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              resolve(axiosInstance(originalRequest));
            });
          });
        }
      }

      return Promise.reject(error);
    }
  );
}

/**
 * Setup interceptors for RTK Query
 * Note: RTK Query handles token refresh via baseQueryWithReauth.js middleware
 */
export function setupRTKQueryInterceptor() {
  // RTK Query handles this differently through its middleware
  // See: Features/API/baseQueryWithReauth.js
}

// Auto-refresh token before it expires
export function setupTokenAutoRefresh() {
  setInterval(async () => {
    const state = store.getState();
    const token = state.auth.token;
    const refreshToken = state.auth.refreshToken;

    if (token && refreshToken) {
      try {
        // Parse JWT to check expiration
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const expiresAt = tokenPayload.exp * 1000; // Convert to milliseconds
        const now = Date.now();
        const timeUntilExpiry = expiresAt - now;

        // Refresh if token expires in less than 1 minute
        if (timeUntilExpiry < 60000 && timeUntilExpiry > 0) {
          const response = await axios.post(`${getAuthBaseUrl()}auth/refresh/`, {
            refresh: refreshToken,
          });

          const { access, refresh } = response.data;

          // Update store with new tokens
          store.dispatch(
            setCredentials({
              token: access,
              refreshToken: refresh || refreshToken,
              user: state.auth.user,
              organizations: state.auth.organizations,
              defaultOrganization: state.auth.defaultOrganization,
              permissions: state.auth.permissions,
              roles: state.auth.roles,
              maxRoleLevel: state.auth.maxRoleLevel,
              currentOrganization: state.auth.currentOrganization,
            })
          );

          // Update session storage
          setToken(access);
          if (refresh) {
            setRefreshToken(refresh);
          }
        }
      } catch {
        // Silent fail - RTK Query will handle retry if needed
      }
    }
  }, 30000); // Check every 30 seconds
}
